import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api from '../services/api';

/**
 * PillarTrendChart — the Phase-3 per-pillar trend chart. Same visual language
 * as the dashboard Wellness IQ sparkline, but for ONE pillar. Reads
 * /api/wellness/iq/trend?pillar=<key> (daily snapshots). Honest "building"
 * state until ≥2 days of that pillar exist.
 */
const W = 600;
const H = 96;

const PillarTrendChart = ({ pillar, color = '#CCFF00', label = 'Trend', days = 14 }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => api.get(`/wellness/iq/trend?days=${days}&pillar=${pillar}`)
      .then(r => setTrend(r.data)).catch(() => {}).finally(() => setLoading(false));
    load();
    const onRefresh = () => load();
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, [pillar, days]);

  if (loading) return <div className="hpme-card p-5 h-[150px] animate-pulse" data-testid={`pillar-trend-loading-${pillar}`} />;

  const points = trend?.points || [];

  if (points.length < 2) {
    return (
      <div className="hpme-card p-5" data-testid={`pillar-trend-building-${pillar}`}>
        <div className="hpme-card-corner" />
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">{label} · trend</p>
          <TrendingUp className="w-5 h-5 text-zinc-600" />
        </div>
        <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
          Your {label.toLowerCase()} line builds here as you check in — so you can see whether you're trending up, not just today's number.
        </p>
      </div>
    );
  }

  const values = points.map(p => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = (max - min) || 1;
  const pad = 8;
  const innerH = H - pad * 2;
  const step = W / (values.length - 1);
  const xy = values.map((v, i) => [i * step, pad + innerH - ((v - min) / range) * innerH]);
  const linePts = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const areaPts = `0,${H} ${linePts} ${W},${H}`;
  const [lastX, lastY] = xy[xy.length - 1];

  const delta = trend?.delta;
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const deltaColor = delta == null || delta === 0 ? '#a1a1aa' : delta > 0 ? '#a3e635' : '#fb923c';
  const gid = `pillarGrad-${pillar}`;

  return (
    <div className="hpme-card p-5 relative overflow-hidden" data-testid={`pillar-trend-${pillar}`}>
      <div className="hpme-card-corner" />
      <div className="flex items-start justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary font-bold">{label} · {points.length}-day trend</p>
        <div className="flex items-center gap-2">
          <span className="font-secondary font-bold text-2xl leading-none" style={{ color }} data-testid={`pillar-trend-current-${pillar}`}>{trend.current}</span>
          {delta != null && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-sm" style={{ color: deltaColor, backgroundColor: `${deltaColor}1a` }} data-testid={`pillar-trend-delta-${pillar}`}>
              <DeltaIcon className="w-3 h-3" />{delta > 0 ? '+' : ''}{delta}
            </span>
          )}
        </div>
      </div>

      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={areaPts} fill={`url(#${gid})`} />
        <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lastX} cy={lastY} r="4.5" fill={color} />
        <circle cx={lastX} cy={lastY} r="9" fill={color} opacity="0.18" />
      </svg>

      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
        <span>low {min} · high {max}</span>
        <span style={{ color: deltaColor }}>
          {delta == null || delta === 0 ? 'holding steady' : delta > 0 ? `up ${delta}` : `down ${Math.abs(delta)}`}
        </span>
      </div>
    </div>
  );
};

export default PillarTrendChart;
